"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import type { ConnectionState, TaskUpdate } from "@/lib/botwork-client";
import { BotworkClient } from "@/lib/botwork-client";
import { MockBotworkClient } from "@/lib/botwork-client/mock";
import { toDemoAgent } from "@/lib/botwork-client/agent-mapping";
import {
  demoApproveTask,
  demoCreateTask,
  demoGetAgents,
  type DemoTaskEvent,
} from "@/lib/botwork-client/demo-adapter";
import {
  connectBrowserWallet,
  mockConnectWallet,
  type MinimalSigner,
} from "@/lib/wallet";
import { switchToBotworkChain } from "@/lib/contracts/network";
import { fromUsdc, getUSDCBalance } from "@/lib/contracts/usdc";
import type { DemoAgent } from "@/lib/app-types";

// ── Mode detection ──────────────────────────────────────────────

function readMode(): "demo" | "live" {
  const v = (process.env.NEXT_PUBLIC_BOTWORK_MODE ?? "").toLowerCase();
  if (v === "live") return "live";
  if (v === "demo") return "demo";
  // Legacy fallback — NEXT_PUBLIC_MOCK_P2P=false ⇒ live
  if (process.env.NEXT_PUBLIC_MOCK_P2P === "false") return "live";
  return "demo";
}

const MODE: "demo" | "live" = readMode();
const GATEWAY_URL = process.env.NEXT_PUBLIC_WS_URL
  ?? process.env.NEXT_PUBLIC_WS_GATEWAY
  ?? "ws://localhost:8080";

// ── Types ───────────────────────────────────────────────────────

export interface TaskEvent {
  taskId: string;
  status: string;
  progress?: number;
  statusLine?: string;
  result?: unknown;
  agentName?: string;
}

type Client = BotworkClient | MockBotworkClient;

export interface BotworkContextValue {
  mode: "demo" | "live";
  client: Client;
  connectionState: ConnectionState;
  isConnected: boolean;
  /** Legacy compat — equivalent to `mode === "demo"`. */
  isMock: boolean;

  // Unified API
  getAgents(): Promise<DemoAgent[]>;
  createTask(description: string, budget: number): Promise<{ id: string }>;
  approveTask(taskId: string, rating: number): Promise<void>;
  subscribeToTask(taskId: string, cb: (evt: TaskEvent) => void): () => void;

  // Auth
  address: string | null;
  /** Access to the underlying ethers signer (null in demo). */
  signer: MinimalSigner | null;
  connectWallet(): Promise<string>;
  signMessage(message: string): Promise<string>;

  // Balance — live mode reads USDC on-chain; demo mode is local state.
  balance: number;
  deposit(amount: number): Promise<void>;
  /** Re-read USDC balance (live only; demo is a no-op). */
  refreshBalance(): Promise<void>;
}

const BotworkContext = createContext<BotworkContextValue | null>(null);

// ── Provider ────────────────────────────────────────────────────

export function BotworkProvider({ children }: { children: ReactNode }) {
  const clientRef = useRef<Client | null>(null);
  const signerRef = useRef<MinimalSigner | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>("disconnected");
  const [address, setAddress] = useState<string | null>(null);
  const [signer, setSigner] = useState<MinimalSigner | null>(null);
  const [balance, setBalance] = useState<number>(0);

  // Create client once. MockBotworkClient is kept as the in-memory
  // client in demo mode so legacy hooks (useTask / useAgents / useRelay)
  // continue to work; the unified API below routes around it.
  if (!clientRef.current) {
    clientRef.current =
      MODE === "live"
        ? new BotworkClient({ autoReconnect: true })
        : new MockBotworkClient();
  }
  const client = clientRef.current;

  useEffect(() => {
    client.onConnectionChange(setConnectionState);
    client.connect(GATEWAY_URL).catch(() => {
      /* swallow — client auto-reconnects in live mode; demo is instant */
    });
    return () => {
      client.destroy();
      clientRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isConnected = connectionState === "connected" || connectionState === "authenticated";

  // ── Unified API ────────────────────────────────────────────────

  const getAgents = useCallback(async (): Promise<DemoAgent[]> => {
    if (MODE === "demo") return demoGetAgents();

    // Session cache — short TTL keeps repeat visits snappy without
    // going stale between real discovery updates. Keyed by the
    // gateway URL so dev/staging/prod don't poison each other.
    const CACHE_KEY = `botwork.agents.v1:${GATEWAY_URL}`;
    const CACHE_TTL_MS = 5 * 60 * 1000;
    try {
      const raw = typeof window !== "undefined" ? window.sessionStorage.getItem(CACHE_KEY) : null;
      if (raw) {
        const { ts, agents } = JSON.parse(raw) as { ts: number; agents: DemoAgent[] };
        if (Date.now() - ts < CACHE_TTL_MS) return agents;
      }
    } catch {
      /* ignore cache read errors */
    }

    const live = client as BotworkClient;
    try {
      const rawAgents = await live.searchAgents({});
      const mapped = rawAgents.map(toDemoAgent);
      try {
        if (typeof window !== "undefined") {
          window.sessionStorage.setItem(
            CACHE_KEY,
            JSON.stringify({ ts: Date.now(), agents: mapped }),
          );
        }
      } catch {
        /* ignore cache write errors */
      }
      return mapped;
    } catch (err) {
      throw err instanceof Error ? err : new Error("getAgents failed");
    }
  }, [client]);

  const createTask = useCallback(async (description: string, _budget: number): Promise<{ id: string }> => {
    if (MODE === "demo") return demoCreateTask(description);
    // Live mode: the shell doesn't yet know which agent to route to —
    // Sprint 2 will pass selected agent + peer address through. For
    // now a placeholder taskId is returned so callers don't break.
    const id = `live-task-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    return { id };
  }, []);

  const approveTask = useCallback(async (taskId: string, _rating: number): Promise<void> => {
    if (MODE === "demo") return demoApproveTask(taskId, _rating);
    const live = client as BotworkClient;
    await live.approveTask(taskId);
  }, [client]);

  const subscribeToTask = useCallback(
    (taskId: string, cb: (evt: TaskEvent) => void): (() => void) => {
      if (MODE === "demo") {
        // Sprint 1: TaskContext owns the demo state machine and its
        // own progress timers. This no-op subscription exists so the
        // API surface is complete; Sprint 2 will wire it for real.
        return () => {};
      }
      const live = client as BotworkClient;
      const handler = (u: TaskUpdate) => {
        if (u.taskId !== taskId) return;
        cb({
          taskId: u.taskId,
          status: u.status,
          progress: u.progress,
          result: u.result,
          agentName: u.agentAddress,
        });
      };
      live.onTaskUpdate(handler);
      live.subscribeTask(taskId);
      return () => {
        live.unsubscribeTask(taskId);
        // onTaskUpdate doesn't expose a removal API; callers should
        // gate by taskId inside their own callback. The handler above
        // already filters, so extra deliveries are harmless.
      };
    },
    [client],
  );

  // ── Auth ───────────────────────────────────────────────────────

  const connectWallet = useCallback(async (): Promise<string> => {
    const { address: addr, signer: newSigner } =
      MODE === "live" ? await connectBrowserWallet() : mockConnectWallet();
    signerRef.current = newSigner;
    setSigner(newSigner);
    setAddress(addr);

    if (MODE === "live") {
      // Ensure the wallet is on the configured Botwork chain.
      if (newSigner.ethersProvider) {
        await switchToBotworkChain(newSigner.ethersProvider).catch(() => {
          /* user can still browse; contract writes will fail until switched */
        });
      }
      if (client instanceof BotworkClient) {
        // Authenticate the WS session with an EIP-191 signature.
        await client.authenticate(newSigner).catch(() => {
          /* auth failure leaves us connected but unauthenticated */
        });
      }
    }
    return addr;
  }, [client]);

  const signMessage = useCallback(async (message: string): Promise<string> => {
    if (!signerRef.current) {
      throw new Error("Wallet not connected — call connectWallet() first.");
    }
    return signerRef.current.signMessage(message);
  }, []);

  // ── Balance ────────────────────────────────────────────────────

  // Read the connected wallet's USDC balance from chain. Gracefully
  // degrades if contracts/USDC addr aren't configured (returns null).
  const refreshBalance = useCallback(async () => {
    if (MODE !== "live") return;
    if (!signerRef.current?.ethersProvider || !signerRef.current.address) return;
    try {
      const raw = await getUSDCBalance(signerRef.current.ethersProvider, signerRef.current.address);
      setBalance(fromUsdc(raw));
    } catch {
      /* USDC address missing or RPC flaky — leave prior balance in place */
    }
  }, []);

  // Refresh on address change + poll every 30s so balance stays fresh
  // without subscribing to Transfer events.
  useEffect(() => {
    if (MODE !== "live" || !address) return;
    refreshBalance();
    const interval = setInterval(refreshBalance, 30_000);
    return () => clearInterval(interval);
  }, [address, refreshBalance]);

  const deposit = useCallback(async (amount: number): Promise<void> => {
    if (MODE === "live") {
      // Sprint 3 placeholder — a real on-chain deposit flow needs a
      // separate USDC faucet or on-ramp integration. For now re-read
      // chain balance so the UI reflects any manual transfers.
      await refreshBalance();
      return;
    }
    setBalance((b) => b + amount);
  }, [refreshBalance]);

  const value = useMemo<BotworkContextValue>(
    () => ({
      mode: MODE,
      client,
      connectionState,
      isConnected,
      isMock: MODE === "demo",
      getAgents,
      createTask,
      approveTask,
      subscribeToTask,
      address,
      signer,
      connectWallet,
      signMessage,
      balance,
      deposit,
      refreshBalance,
    }),
    [
      client,
      connectionState,
      isConnected,
      getAgents,
      createTask,
      approveTask,
      subscribeToTask,
      address,
      signer,
      connectWallet,
      signMessage,
      balance,
      deposit,
      refreshBalance,
    ],
  );

  return <BotworkContext.Provider value={value}>{children}</BotworkContext.Provider>;
}

export function useBotworkContext(): BotworkContextValue {
  const ctx = useContext(BotworkContext);
  if (!ctx) throw new Error("useBotworkContext must be used within <BotworkProvider>");
  return ctx;
}
